import { useState, useCallback, useRef, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { NewChatView } from "./new-chat-view"
import { ChatHeader } from "./chat-header"
import { ChatArchive } from "./chat-archive"
import { ChatThread } from "./chat-thread"
import { ChatComposer, type ChatAttachmentReference, type ModelSpeed } from "./chat-composer"
import { useAgentWebSocket, type ChatDocumentScope, type DocumentCitationData, type WSConfirmRequiredMessage } from "hooks/useAgentWebSocket"
import { ConfirmActionDialog } from "../common/ConfirmActionDialog"
import { useAgents } from "hooks/useAgents"
import { useConversations } from "hooks/useConversations"
import { getChatTitle, uploadChatAttachment } from "../../services/ai_agent"
import { getDocumentStoragePolicy, listDocumentSpaces } from "services/documents"
import type { DocumentSpace } from "types/documents"
import { useAgentPageContext } from "contexts/AgentPageContext"

interface AgentChatPanelProps {
  isOpen: boolean
  onClose: () => void
  mode?: "floating" | "embedded"
}

type ChatItem = {
  id: string
  title: string
  lastMessage: string
  timestamp: Date
  unread: number
  group: string
}

type AttachmentInfo = {
  name: string
  type: string
  size: number
  url?: string
}

type MessageItem = {
  id: string
  type: "user" | "agent"
  content: string
  timestamp: Date
  attachments?: AttachmentInfo[]
  /** Tool currently being executed (shown as status) */
  toolCall?: string
  /** Whether the agent is still streaming */
  isStreaming?: boolean
  citations?: DocumentCitationData[]
}

type View = "new" | "thread" | "archive"

/** Assign a group label based on date */
function dateToGroup(date: Date): string {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date >= today) return "Oggi"
  if (date >= yesterday) return "Ieri"
  return "Precedenti"
}

export function AgentChatPanel({ isOpen, onClose, mode = "floating" }: AgentChatPanelProps) {
  const isEmbedded = mode === "embedded"
  const navigate = useNavigate()
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null)
  const [localChats, setLocalChats] = useState<ChatItem[]>([])
  const [messagesByChatId, setMessagesByChatId] = useState<Record<string, MessageItem[]>>({})
  const [inputValue, setInputValue] = useState("")
  const [view, setView] = useState<View>("new")
  const [isExpanded, setIsExpanded] = useState(false)
  const [previousView, setPreviousView] = useState<View>("new")
  const isLoading = false
  const [awaitingFirstToken, setAwaitingFirstToken] = useState(false)
  const [modelSpeed, setModelSpeed] = useState<ModelSpeed>('fast')
  const [pendingConfirm, setPendingConfirm] = useState<WSConfirmRequiredMessage | null>(null)
  const [confirmBusy, setConfirmBusy] = useState(false)
  const [documentSpaces, setDocumentSpaces] = useState<DocumentSpace[]>([])
  const [chatAttachmentMaxBytes, setChatAttachmentMaxBytes] = useState<number>()

  const handleDocumentOpen = useCallback((path: string) => {
    // Nel widget flottante chiudiamo prima la chat e navighiamo nel frame
    // successivo: la route cambia dentro la SPA, senza il flash da reload.
    if (!isEmbedded) {
      onClose()
      requestAnimationFrame(() => navigate(path))
      return
    }
    navigate(path)
  }, [isEmbedded, navigate, onClose])

  useEffect(() => {
    if (!isOpen) return
    void getDocumentStoragePolicy()
      .then((policy) => setChatAttachmentMaxBytes(policy.chat_attachment_max_bytes))
      .catch(() => setChatAttachmentMaxBytes(undefined))
  }, [isOpen])
  const [scopesByChatId, setScopesByChatId] = useState<Record<string, ChatDocumentScope>>({})
  const [draftScope, setDraftScope] = useState<ChatDocumentScope>({ mode: "UNSET" })
  const { documentContext } = useAgentPageContext()
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    void listDocumentSpaces().then(setDocumentSpaces).catch(() => setDocumentSpaces([]))
  }, [isOpen])

  useEffect(() => {
    if (selectedChatId || !documentContext) return
    setDraftScope({
      mode: "SPACE", space_id: documentContext.suggestedSpaceId,
      space_name: documentContext.suggestedSpaceName,
      space_type: documentContext.suggestedSpaceType,
      source: "UI_PAGE_SUGGESTION",
    })
  }, [documentContext, selectedChatId])

  // Track newly created chat IDs for background title generation
  const newChatIdsRef = useRef<Set<string>>(new Set())
  const draftChatIdRef = useRef<string | null>(null)

  // Agent selection
  const { agents, selectedAgent, selectAgent, isLoading: isLoadingAgents } = useAgents()

  // Conversations from API
  const {
    conversations,
    isLoading: isLoadingConversations,
    isLoadingMessages,
    fetchConversations,
    deleteConversation,
    loadMessages,
    selectConversation,
  } = useConversations()

  // Ref to track the streaming message id per chat for token accumulation
  const streamingMsgIdRef = useRef<Record<string, string>>({})

  // ============================================================
  // Merge API conversations with local (in-memory) chats
  // ============================================================
  const mergedChats: ChatItem[] = [
    // Local chats first (newly created, not yet persisted)
    ...localChats.filter((lc) => !conversations.some((c) => c.chat_id === lc.id)),
    // Then API conversations mapped to ChatItem format
    ...conversations.map((conv) => ({
      id: conv.chat_id,
      title: conv.title || "Chat senza titolo",
      lastMessage: "",
      timestamp: new Date(conv.last_message_at || conv.created_at),
      unread: 0,
      group: dateToGroup(new Date(conv.last_message_at || conv.created_at)),
    })),
  ]

  // Message counts per chat for archive subtitle
  const messagesCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const [chatId, msgs] of Object.entries(messagesByChatId)) {
      counts[chatId] = msgs.length
    }
    return counts
  }, [messagesByChatId])

  // Refresh conversation list when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchConversations()
    }
  }, [isOpen, fetchConversations])

  // In focus mode, Escape first restores the compact panel. A second Escape
  // closes it, so users never lose the conversation context unexpectedly.
  useEffect(() => {
    if (!isOpen || isEmbedded) return
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      if (isExpanded) setIsExpanded(false)
      else onClose()
    }
    window.addEventListener("keydown", handleEscape)
    return () => window.removeEventListener("keydown", handleEscape)
  }, [isOpen, isEmbedded, isExpanded, onClose])

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (view === "thread") {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }
  }, [messagesByChatId, selectedChatId, view])

  // Reset typing flag on chat switch (not on initial assignment)
  const prevSelectedChatIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (
      prevSelectedChatIdRef.current !== null &&
      prevSelectedChatIdRef.current !== selectedChatId
    ) {
      setAwaitingFirstToken(false)
    }
    prevSelectedChatIdRef.current = selectedChatId
  }, [selectedChatId])

  // Safety timeout: if no event arrives within 30s, surface a gentle error
  useEffect(() => {
    if (!awaitingFirstToken) return
    const timer = setTimeout(() => {
      setAwaitingFirstToken(false)
      if (!selectedChatId) return
      setMessagesByChatId((prev) => ({
        ...prev,
        [selectedChatId]: [
          ...(prev[selectedChatId] || []),
          {
            id: `m-${Date.now()}-timeout`,
            type: "agent" as const,
            content: "Sembra che l'agent non risponda. Riprova più tardi.",
            timestamp: new Date(),
          },
        ],
      }))
    }, 30000)
    return () => clearTimeout(timer)
  }, [awaitingFirstToken, selectedChatId])

  // ============================================================
  // WebSocket callbacks
  // ============================================================

  const handleToken = useCallback((content: string, chatId: string) => {
    setAwaitingFirstToken(false)
    const existingId = streamingMsgIdRef.current[chatId]

    if (existingId) {
      // Append token to existing streaming message.
      // A token event means the tool loop ended and the LLM is now narrating,
      // so clear any tool status that was being displayed.
      setMessagesByChatId((prev) => {
        const msgs = prev[chatId] || []
        return {
          ...prev,
          [chatId]: msgs.map((m) =>
            m.id === existingId ? { ...m, content: m.content + content, toolCall: undefined } : m
          ),
        }
      })
    } else {
      // First token: set ref BEFORE setState to keep callback pure
      const newId = `m-agent-${Date.now()}`
      streamingMsgIdRef.current[chatId] = newId
      setMessagesByChatId((prev) => {
        const msgs = prev[chatId] || []
        return {
          ...prev,
          [chatId]: [
            ...msgs,
            {
              id: newId,
              type: "agent" as const,
              content: content,
              timestamp: new Date(),
              isStreaming: true,
            },
          ],
        }
      })
    }
  }, [])

  const handleToolCall = useCallback((tool: string, chatId: string) => {
    setAwaitingFirstToken(false)
    const existingId = streamingMsgIdRef.current[chatId]

    if (existingId) {
      setMessagesByChatId((prev) => {
        const msgs = prev[chatId] || []
        return {
          ...prev,
          [chatId]: msgs.map((m) =>
            m.id === existingId ? { ...m, toolCall: tool } : m
          ),
        }
      })
    } else {
      const newId = `m-agent-${Date.now()}`
      streamingMsgIdRef.current[chatId] = newId
      setMessagesByChatId((prev) => {
        const msgs = prev[chatId] || []
        return {
          ...prev,
          [chatId]: [
            ...msgs,
            {
              id: newId,
              type: "agent" as const,
              content: "",
              timestamp: new Date(),
              toolCall: tool,
              isStreaming: true,
            },
          ],
        }
      })
    }
  }, [])

  const handleFormField = useCallback((_field: string, prompt: string, chatId: string) => {
    setAwaitingFirstToken(false)
    setMessagesByChatId((prev) => {
      const msgs = prev[chatId] || []
      return {
        ...prev,
        [chatId]: [
          ...msgs,
          {
            id: `m-form-${Date.now()}`,
            type: "agent" as const,
            content: prompt,
            timestamp: new Date(),
          },
        ],
      }
    })
  }, [])

  const handleComplete = useCallback((chatId: string) => {
    setAwaitingFirstToken(false)

    // Close confirm dialog if it was tied to this chat. The backend only
    // sends `complete` when the flow is actually over (paused turns gate it),
    // so it's safe to dismiss here without flicker on confirmation chains.
    setPendingConfirm((prev) => {
      if (prev && prev.chat_id === chatId) {
        setConfirmBusy(false)
        return null
      }
      return prev
    })

    // Mark the streaming message as complete
    const streamingId = streamingMsgIdRef.current[chatId]
    if (streamingId) {
      setMessagesByChatId((prev) => {
        const msgs = prev[chatId] || []
        return {
          ...prev,
          [chatId]: msgs.map((m) =>
            m.id === streamingId
              ? { ...m, isStreaming: false, toolCall: undefined }
              : m
          ),
        }
      })
      delete streamingMsgIdRef.current[chatId]
    }

    // Update the local chat's last message preview
    setLocalChats((prev) =>
      prev.map((chat) => {
        if (chat.id !== chatId) return chat
        const msgs = messagesByChatId[chatId] || []
        const lastAgentMsg = [...msgs].reverse().find((m) => m.type === "agent")
        return {
          ...chat,
          lastMessage: lastAgentMsg?.content?.substring(0, 50) || chat.lastMessage,
        }
      })
    )

    // Generate title in background for new chats
    if (newChatIdsRef.current.has(chatId)) {
      newChatIdsRef.current.delete(chatId)
      const msgs = messagesByChatId[chatId] || []
      const firstUserMsg = msgs.find((m) => m.type === "user")
      if (firstUserMsg) {
        getChatTitle(firstUserMsg.content, chatId)
          .then((titleRes) => {
            const chatTitle = titleRes.data?.chat_title
            if (chatTitle) {
              setLocalChats((prev) =>
                prev.map((c) => (c.id === chatId ? { ...c, title: chatTitle } : c))
              )
            }
          })
          .catch(() => {})
      }
    }

    // Refresh conversation list from API
    fetchConversations()
  }, [messagesByChatId, fetchConversations])

  const handleCitations = useCallback((citations: DocumentCitationData[], chatId: string) => {
    const streamingId = streamingMsgIdRef.current[chatId]
    if (!streamingId) return
    setMessagesByChatId((prev) => ({
      ...prev,
      [chatId]: (prev[chatId] || []).map((message) =>
        message.id === streamingId ? { ...message, citations } : message
      ),
    }))
  }, [])

  const handleWSError = useCallback((content: string, chatId: string) => {
    setAwaitingFirstToken(false)

    setPendingConfirm((prev) => {
      if (prev && prev.chat_id === chatId) {
        setConfirmBusy(false)
        return null
      }
      return prev
    })

    const streamingId = streamingMsgIdRef.current[chatId]
    if (streamingId) {
      setMessagesByChatId((prev) => {
        const msgs = prev[chatId] || []
        return {
          ...prev,
          [chatId]: msgs.map((m) =>
            m.id === streamingId ? { ...m, isStreaming: false, toolCall: undefined } : m
          ),
        }
      })
      delete streamingMsgIdRef.current[chatId]
    }

    setMessagesByChatId((prev) => {
      const msgs = prev[chatId] || []
      return {
        ...prev,
        [chatId]: [
          ...msgs,
          {
            id: `m-err-${Date.now()}`,
            type: "agent" as const,
            content: `Errore: ${content}`,
            timestamp: new Date(),
          },
        ],
      }
    })
  }, [])

  // When a (new) confirm_required arrives, replace the modal and explicitly
  // reset busy: in confirmation chains (e.g. delete_task -> resume ->
  // delete_contact) the first click set busy=true; the backend then emits
  // a fresh confirm_required without an intervening complete/error, and
  // without this reset the second modal would render with disabled buttons.
  const handleConfirmRequired = useCallback((msg: WSConfirmRequiredMessage) => {
    setPendingConfirm(msg)
    setConfirmBusy(false)
  }, [])

  // ============================================================
  // WebSocket hook
  // ============================================================
  const { sendMessage, sendConfirmResponse, sendDocumentScope, connectionState } = useAgentWebSocket({
    autoConnect: isOpen,
    onToken: handleToken,
    onToolCall: handleToolCall,
    onFormField: handleFormField,
    onComplete: handleComplete,
    onError: handleWSError,
    onConfirmRequired: handleConfirmRequired,
    onCitations: handleCitations,
    onDocumentScopeChanged: (scope, chatId) => setScopesByChatId((prev) => {
      const current = prev[chatId]
      if ((scope.version ?? 0) < (current?.version ?? 0)) return prev
      return { ...prev, [chatId]: scope }
    }),
  })

  const handleConfirmAccept = useCallback(() => {
    if (!pendingConfirm) return
    const ok = sendConfirmResponse({
      op_id: pendingConfirm.op_id,
      chat_id: pendingConfirm.chat_id,
      accept: true,
    })
    if (ok) setConfirmBusy(true)
    // If ok=false (WS down) the modal stays interactive so the user can retry.
  }, [pendingConfirm, sendConfirmResponse])

  const handleConfirmCancel = useCallback(() => {
    if (!pendingConfirm) return
    const ok = sendConfirmResponse({
      op_id: pendingConfirm.op_id,
      chat_id: pendingConfirm.chat_id,
      accept: false,
    })
    if (ok) setConfirmBusy(true)
  }, [pendingConfirm, sendConfirmResponse])

  // ============================================================
  // Chat actions
  // ============================================================

  const handleQuickAction = (actionId: string) => {
    const prompt =
      actionId === "brief-today"
        ? "Fammi il brief di oggi: meeting in calendario, task in scadenza e notifiche importanti. Sii conciso."
        : actionId === "focus-now"
          ? "Qual è l'attività più prioritaria su cui dovrei concentrarmi adesso? Considera scadenze e importanza e suggeriscine una sola."
          : ""
    if (!prompt) return
    void handleSendMessage(prompt, [])
  }

  const handleRegenerate = (messageId: string) => {
    if (!selectedChatId) return
    const currentList = messagesByChatId[selectedChatId] || []
    const idx = currentList.findIndex((m) => m.id === messageId)
    if (idx === -1) return

    const target = currentList[idx]
    let userPrompt: string
    let truncateIdx: number

    if (target.type === "agent") {
      // Find the most recent user message before this agent message
      const precedingUser = currentList
        .slice(0, idx)
        .reverse()
        .find((m) => m.type === "user")
      if (!precedingUser) return
      userPrompt = precedingUser.content
      truncateIdx = currentList.indexOf(precedingUser)
    } else {
      // Target is a user message: truncate to just before it and resend its content
      userPrompt = target.content
      truncateIdx = idx
    }

    setMessagesByChatId((prev) => ({
      ...prev,
      [selectedChatId]: currentList.slice(0, truncateIdx),
    }))

    void handleSendMessage(userPrompt, [])
  }

  const resolveDraftChatId = useCallback(() => {
    if (selectedChatId) return selectedChatId
    if (!draftChatIdRef.current) {
      draftChatIdRef.current = `chat-${Date.now()}`
    }
    return draftChatIdRef.current
  }, [selectedChatId])

  const handleUploadFile = useCallback(async (file: File): Promise<ChatAttachmentReference> => {
    const chatId = resolveDraftChatId()
    const formData = new FormData()
    formData.append("chat_id", chatId)
    formData.append("file", file)
    const res = await uploadChatAttachment(formData)
    return {
      attachment_id: res.data.attachment_id,
      original_name: res.data.original_name,
      size: res.data.size,
      expires_at: res.data.expires_at,
      content_type: res.data.content_type,
      type: file.type,
    }
  }, [resolveDraftChatId])

  const handleSendMessage = async (message: string, attachments: ChatAttachmentReference[] = []) => {
    if (!message.trim() && attachments.length === 0) return

    const isNewChat = !selectedChatId
    const chatId = selectedChatId || draftChatIdRef.current || `chat-${Date.now()}`
    draftChatIdRef.current = null

    // Build attachment metadata for the local message
    const userAttachments: AttachmentInfo[] = attachments.map((att) => ({
      name: att.original_name,
      type: att.type || att.content_type || "",
      size: att.size,
      url: att.previewUrl,
    }))

    // Create new chat if needed
    if (isNewChat) {
      const newChat: ChatItem = {
        id: chatId,
        title: message.substring(0, 30) || attachments[0]?.original_name || "Nuova chat",
        lastMessage: message || `${attachments.length} file allegati`,
        timestamp: new Date(),
        unread: 0,
        group: "Oggi",
      }
      setLocalChats((prev) => [newChat, ...prev])
      setSelectedChatId(chatId)
      selectConversation(chatId)
      newChatIdsRef.current.add(chatId)
    }

    // Add user message immediately for instant feedback
    const userMsg: MessageItem = {
      id: `m-${Date.now()}-1`,
      type: "user",
      content: message,
      timestamp: new Date(),
      attachments: userAttachments.length > 0 ? userAttachments : undefined,
    }

    setMessagesByChatId((prev) => ({
      ...prev,
      [chatId]: [...(prev[chatId] || []), userMsg],
    }))

    setView("thread")
    setInputValue("")

    const safeAttachments = attachments.map((att) => ({
      attachment_id: att.attachment_id,
      original_name: att.original_name,
      size: att.size,
    }))

    const sent = sendMessage({
      text: message || "Salva l'allegato nel dossier documentale indicato.",
      chat_id: chatId,
      agent_id: selectedAgent?.slug,
      model_speed: modelSpeed,
      attachments: safeAttachments,
      document_scope: isNewChat ? draftScope : undefined,
    })
    if (sent) {
      setAwaitingFirstToken(true)
    } else {
        setMessagesByChatId((prev) => ({
          ...prev,
          [chatId]: [
            ...(prev[chatId] || []),
            {
              id: `m-${Date.now()}-err`,
              type: "agent" as const,
              content: "L'assistente AI non è al momento disponibile. Riprova più tardi.",
              timestamp: new Date(),
            },
          ],
        }))
    }
  }

  const handleSelectChat = async (chatId: string) => {
    draftChatIdRef.current = null
    setSelectedChatId(chatId)
    selectConversation(chatId)
    setView("thread")

    // Load messages from API if not already in memory
    if (!messagesByChatId[chatId] || messagesByChatId[chatId].length === 0) {
      const apiMessages = await loadMessages(chatId)
      if (apiMessages.length > 0) {
        const mapped: MessageItem[] = apiMessages.map((m: any) => ({
          id: m.id || `m-api-${Date.now()}-${Math.random()}`,
          type: (m.role === "user" ? "user" : "agent") as "user" | "agent",
          content: m.content,
          timestamp: new Date(m.created_at),
        }))
        setMessagesByChatId((prev) => ({
          ...prev,
          [chatId]: mapped,
        }))
      }
    }
  }

  const handleDeleteChat = async (chatId: string) => {
    const success = await deleteConversation(chatId)
    if (success) {
      setLocalChats((prev) => prev.filter((c) => c.id !== chatId))
      setMessagesByChatId((prev) => {
        const next = { ...prev }
        delete next[chatId]
        return next
      })
      if (selectedChatId === chatId) {
        setSelectedChatId(null)
        selectConversation(null)
        setView("new")
      }
    }
  }

  const handleNewChat = () => {
    draftChatIdRef.current = null
    setSelectedChatId(null)
    selectConversation(null)
    setDraftScope(documentContext ? {
      mode: "SPACE", space_id: documentContext.suggestedSpaceId,
      space_name: documentContext.suggestedSpaceName,
      space_type: documentContext.suggestedSpaceType,
      source: "UI_PAGE_SUGGESTION",
    } : { mode: "UNSET" })
    setView("new")
  }

  const handleToggleArchive = () => {
    setPreviousView(view)
    setView("archive")
  }

  const handleBackFromArchive = () => {
    setView(previousView)
  }

  // Derive the title for the header
  const currentChatTitle = selectedChatId
    ? mergedChats.find((c) => c.id === selectedChatId)?.title ?? null
    : null

  const currentMessages = selectedChatId ? messagesByChatId[selectedChatId] || [] : []
  const displayedScope = selectedChatId ? (scopesByChatId[selectedChatId] || { mode: "UNSET" as const }) : draftScope
  const handleScopeSelection = (value: string) => {
    let scope: ChatDocumentScope
    if (value === "ALL") scope = { mode: "ALL", source: "EXPLICIT_ALL" }
    else if (value === "UNSET") scope = { mode: "UNSET" }
    else {
      const space = documentSpaces.find((item) => item.id === Number(value))
      if (!space) return
      scope = { mode: "SPACE", space_id: space.id, space_name: space.name, space_type: space.space_type, source: "UI_SELECTION" }
    }
    if (selectedChatId) sendDocumentScope(selectedChatId, scope)
    else setDraftScope(scope)
  }
  const scopeSelector = (
    <label className="mb-2 flex items-center gap-2 text-xs text-[var(--hexa-text-muted)]">
      <span>Cerca in:</span>
      <select
        aria-label="Scope documentale"
        value={displayedScope.mode === "SPACE" ? String(displayedScope.space_id) : displayedScope.mode}
        onChange={(event) => handleScopeSelection(event.target.value)}
        className="min-w-0 flex-1 rounded-lg border bg-[var(--hexa-surface-elevated)] px-2 py-1.5 text-[var(--hexa-text-primary)]"
      >
        <option value="UNSET">Scegli un archivio...</option>
        {documentSpaces.map((space) => <option key={space.id} value={space.id}>{space.space_type === "GENERAL" ? "Archivio aziendale" : `Dossier ${space.name}`}</option>)}
        <option value="ALL">Tutti gli archivi</option>
      </select>
    </label>
  )

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop — only in floating mode */}
      {!isEmbedded && (
        <div
          className={`fixed inset-0 z-40 transition-colors duration-300 ${isExpanded ? "bg-black/35 backdrop-blur-[2px]" : "bg-black/10"}`}
          onClick={onClose}
        />
      )}

      <div className={
        isEmbedded
          ? "min-w-0 w-full h-full bg-[var(--hexa-card-bg)] border border-[var(--hexa-border)] rounded-xl flex flex-col overflow-hidden"
          : isExpanded
            ? "fixed inset-x-3 top-3 h-[calc(100dvh-24px)] sm:top-auto sm:bottom-6 sm:left-auto sm:right-6 sm:w-[min(880px,calc(100vw-48px))] sm:h-[min(820px,calc(100dvh-48px))] bg-[var(--hexa-card-bg)] backdrop-blur-md border border-[var(--hexa-border)] rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden transition-[width,height] duration-300 ease-out"
            : "fixed inset-x-3 top-3 h-[calc(100dvh-24px)] sm:top-auto sm:bottom-6 sm:left-auto sm:right-6 sm:w-96 sm:h-[min(600px,calc(100dvh-48px))] bg-[var(--hexa-card-bg)] backdrop-blur-md border border-[var(--hexa-border)] rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden transition-[width,height] duration-300 ease-out"
      }>
        {/* Header */}
        <ChatHeader
          view={view}
          onToggleArchive={handleToggleArchive}
          onClose={onClose}
          onBackFromArchive={handleBackFromArchive}
          onNewChat={handleNewChat}
          agents={agents}
          selectedAgentId={selectedAgent?.slug ?? null}
          onSelectAgent={selectAgent}
          isLoadingAgents={isLoadingAgents}
          conversationTitle={currentChatTitle}
          showClose={!isEmbedded}
          isExpanded={isExpanded}
          onToggleExpanded={isEmbedded ? undefined : () => setIsExpanded((value) => !value)}
        />

        {/* Connection status indicator */}
        {connectionState !== 'connected' && (
          <div className={`px-4 py-1 text-[10px] text-center flex-shrink-0 ${
            connectionState === 'connecting'
              ? 'bg-yellow-500/10 text-yellow-500'
              : 'bg-red-500/10 text-red-500'
          }`}>
            {connectionState === 'connecting' ? 'Connessione in corso...' : 'Non connesso'}
          </div>
        )}

        {/* Content area */}
        {view === "archive" ? (
          /* Archive — full panel */
          <ChatArchive
            chats={mergedChats}
            selectedChatId={selectedChatId}
            isLoading={isLoadingConversations}
            messagesCounts={messagesCounts}
            onSelectChat={handleSelectChat}
            onDeleteChat={handleDeleteChat}
          />
        ) : (
          /* New / Thread — unified container with composer */
          <div className="flex-1 min-h-0 min-w-0 flex flex-col p-2 sm:p-4">
            <div
              className="flex-1 min-h-0 min-w-0 flex flex-col p-2 sm:p-4"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid var(--hexa-border)",
                borderRadius: "16px",
              }}
            >
              {view === "new" && isEmbedded ? (
                /* Embedded + new chat: greeting + chips + composer centered */
                <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center gap-4 py-2">
                  <NewChatView
                    compact
                    onQuickAction={handleQuickAction}
                  />
                  <div className="w-full min-w-0 shrink-0 mb-auto">
                    {scopeSelector}
                    <ChatComposer
                      value={inputValue}
                      onChange={setInputValue}
                      onSend={handleSendMessage}
                      onUploadFile={handleUploadFile}
                      maxFileBytes={chatAttachmentMaxBytes}
                      modelSpeed={modelSpeed}
                      onModelSpeedChange={setModelSpeed}
                    />
                  </div>
                </div>
              ) : (
                <>
                  {/* Messages / New chat area — scrollable */}
                  <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hexa">
                    {isLoadingMessages ? (
                      <div className="h-full flex items-center justify-center">
                        <p className="text-[var(--hexa-text-muted)] text-sm">Caricamento messaggi...</p>
                      </div>
                    ) : view === "new" ? (
                      <NewChatView onQuickAction={handleQuickAction} />
                    ) : (
                      <>
                        <ChatThread messages={currentMessages} isLoading={isLoading || awaitingFirstToken} onRegenerate={handleRegenerate} onDocumentOpen={handleDocumentOpen} />
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>

                  {/* Composer */}
                  {scopeSelector}
                  <ChatComposer
                    value={inputValue}
                    onChange={setInputValue}
                    onSend={handleSendMessage}
                    onUploadFile={handleUploadFile}
                    maxFileBytes={chatAttachmentMaxBytes}
                    modelSpeed={modelSpeed}
                    onModelSpeedChange={setModelSpeed}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {pendingConfirm && (
        <ConfirmActionDialog
          key={pendingConfirm.op_id}
          open
          entity={pendingConfirm.entity}
          action={pendingConfirm.action}
          label={pendingConfirm.label}
          preview={pendingConfirm.preview}
          expiresAt={pendingConfirm.expires_at}
          busy={confirmBusy}
          onCancel={handleConfirmCancel}
          onConfirm={handleConfirmAccept}
        />
      )}
    </>
  )
}
